import React, { useState } from 'react';
import { Box, Grid, Tabs, Tab, Card, CardContent, Typography, Alert } from '@mui/material';
import { Info } from '@mui/icons-material';
import { PageHeader, LoadingState, ErrorState } from './shared/components';
import { useFeedbackInsights } from './hooks/useFeedbackInsights';
import { InsightMetrics, FeedbackDistributionChart, LearningImpact, PatternAnalysis } from './components';

/**
 * Feedback Analytics - Platform Admin
 * 
 * PLATFORM-LEVEL FEATURE:
 * - Aggregated feedback from ALL tenants (anonymized)
 * - Global ML system health monitoring
 * - Cross-tenant pattern analysis
 * - No tenant_id filtering (platform-wide view)
 * 
 * SECURITY RATIONALE:
 * - Tenant access removed to prevent feedback gaming (27% F1-score degradation risk)
 * - Prevents label poisoning attacks
 * - Platform team monitors global ML health
 * - Tenants see simple "Alert Quality Score" instead
 * 
 * MIGRATED FROM: /src/features/advanced/components/feedback/FeedbackInsights.tsx
 * CHANGES: Replaced placeholders with recovered components (Oct 21, 2025)
 *          - Recovered: InsightMetrics, FeedbackOverview, LearningImpact, PatternAnalysis
 *          - Created NEW useFeedbackInsights hook with REAL API calls (no mock data)
 *          - Removed tenant_id filtering (platform-wide aggregation)
 * 
 * Following 00-READ-THIS-FIRST:
 * - NO FUNCTIONAL DUPLICATES: Replaced placeholders, didn't add duplicates
 * - SINGLE SOURCE OF TRUTH: Components recovered from git, not recreated
 * - NO MOCK DATA: Hook uses real backend APIs
 */

export const FeedbackAnalytics: React.FC = () => {
  const [activeSource, setActiveSource] = useState<'all' | 'analyst' | 'brain'>('all');
  
  // Use REAL data from backend APIs (not mock data)
  const {
    loading,
    error,
    metrics,
    distribution,
    weeklyTrend,
    learningMetrics,
    timeseriesData,
    patterns
  } = useFeedbackInsights();

  if (loading) return <LoadingState message="Loading feedback analytics..." />;
  if (error) return <ErrorState error={error} onRetry={() => window.location.reload()} />;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Feedback Analytics"
        subtitle="Aggregated Cross-Tenant Analytics - Anonymized feedback patterns across all tenants"
        onRefresh={() => window.location.reload()}
      />

      {/* Platform Admin Info Banner */}
      <Alert 
        severity="info" 
        icon={<Info />}
        sx={{ mb: 3, bgcolor: 'info.dark', border: '1px solid', borderColor: 'info.main' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Aggregated Cross-Tenant Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This view shows anonymized feedback patterns across all tenants to identify systematic ML issues and improvement opportunities.
        </Typography>
      </Alert>

      {/* Source Filter Tabs */}
      <Tabs 
        value={activeSource} 
        onChange={(_, value) => setActiveSource(value)} 
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="All Sources" value="all" />
        <Tab label="Analyst Feedback" value="analyst" />
        <Tab label="Brain Feedback" value="brain" />
      </Tabs>

      {/* Real metrics from backend API */}
      <Box sx={{ mb: 3 }}>
        <InsightMetrics metrics={metrics} source={activeSource} />
      </Box>

      {/* Real charts from backend API */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div>
          <FeedbackDistributionChart 
            distribution={distribution}
            weeklyTrend={weeklyTrend}
            source={activeSource}
          />
        </div>
        <div>
          <LearningImpact 
            metrics={learningMetrics}
            timeseriesData={timeseriesData}
            source={activeSource}
          />
        </div>
      </div>

      {/* Real pattern analysis from backend API */}
      <Box sx={{ mb: 3 }}>
        <PatternAnalysis patterns={patterns} source={activeSource} />
      </Box>

      {/* Security Info */}
      <Card sx={{ bgcolor: 'grey.900', border: '1px solid', borderColor: 'grey.800' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 2 }}>
            Why Platform Admin Only?
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, '& li': { color: 'text.secondary', fontSize: '0.875rem', mb: 1 } }}>
            <li>
              <Typography component="span" sx={{ fontWeight: 600, color: 'white' }}>Gaming Prevention:</Typography> Tenants cannot see patterns to manipulate feedback
            </li>
            <li>
              <Typography component="span" sx={{ fontWeight: 600, color: 'white' }}>Label Poisoning:</Typography> Prevents 27% model degradation from strategic marking
            </li>
            <li>
              <Typography component="span" sx={{ fontWeight: 600, color: 'white' }}>Global Insights:</Typography> Platform team identifies systematic issues
            </li>
            <li>
              <Typography component="span" sx={{ fontWeight: 600, color: 'white' }}>Anonymization:</Typography> Tenant data aggregated and anonymized
            </li>
            <li>
              <Typography component="span" sx={{ fontWeight: 600, color: 'white' }}>ML Health:</Typography> Monitor model performance across entire platform
            </li>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeedbackAnalytics;
